import React, { Component, FC, ReactElement, ReactNode, Suspense } from 'react';

import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { reportError } from 'lib/reportError';
import { IRoute } from 'types';
import { Button } from 'views/components/UI/Button/Button';
import { Text } from 'views/components/UI/Text/Text';

class RouteErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: Error): void {
    reportError(error, 'route-render');
  }

  render(): ReactNode {
    const { failed } = this.state;
    const { children } = this.props;
    return failed ? (
      <div role="alert">
        <Text>
          <FormattedMessage id="route-load.error" />
        </Text>
        <Button onClick={() => window.location.reload()}>
          <FormattedMessage id="route-load.reload" />
        </Button>
      </div>
    ) : (
      children
    );
  }
}

const RouteContent: FC<IRoute> = ({ component, pageTitle }): ReactElement => {
  const { key } = useLocation();
  return (
    <RouteErrorBoundary key={key}>
      <Suspense
        fallback={
          <div role="status" aria-busy="true">
            <Text>
              <FormattedMessage id="route-load.loading" />
            </Text>
          </div>
        }
      >
        {React.createElement<{ pageTitle: string }>(component, { pageTitle })}
      </Suspense>
    </RouteErrorBoundary>
  );
};

export { RouteContent };
